import { Loading } from '@/src/common/components/Loading'
import { useLogError } from '@/src/common/hooks/useLogError'
import { getDocumentAsync } from 'expo-document-picker'
import { type ComponentProps, Fragment, useRef, useState, useEffect } from 'react'
import { ChevronWithIcon } from '@/src/common/components/ChevronWithIcon'
import { PassportIcon } from '@/src/assets/icons/PassportIcon'
import { startCase } from 'lodash'
import { TrashIcon } from '@/src/assets/icons/trash'
import AntDesign from '@expo/vector-icons/AntDesign'
import { useAlert } from '@/src/components/ui/elements/dialogs/useAlert'
import { Platform } from 'react-native'
import { Button } from '@/src/common/components/Button'
import { Redirect, router } from 'expo-router'
import { Routes } from '@/src/common/enums/routes'
import { Spinner, View, YStack } from 'tamagui'
import BottomSheet from '@gorhom/bottom-sheet'
import { DesignSystem } from '@/src/design-system'
import { useSardine } from '@/src/services/Sardine'
import { type ExpoDocumentPickerFile, uploadFile } from '@/src/common/utils/uploadFile'
import { mesaColorNames } from '@/src/common/theme'
import { DEFAULT_FILE_TYPES } from '@/src/design-system/modal/UploadModal'
import { cardApplicationStyles } from './_highnote/common'
import * as Components from '../components'
import * as Hooks from '../hooks'
import * as Stores from '../stores'

type DocumentUploadRequirement = NonNullable<
  ReturnType<typeof Hooks.useActiveApplication>['activeApplicationUploads']
>[number]

const DocumentMap = Map<
  DocumentUploadRequirement,
  null | { file: ExpoDocumentPickerFile; uploading?: true }
>

type ChevronWithIconProps = ComponentProps<typeof ChevronWithIcon>

interface DocumentRowProps
  extends Partial<Omit<ChevronWithIconProps, 'text' | 'caption'>>,
    Pick<ChevronWithIconProps, 'caption'> {
  documentType: DocumentUploadRequirement
}

function DocumentRow(props: DocumentRowProps) {
  return (
    <ChevronWithIcon
      segmentAction="User Pressed the Document Upload Item"
      Icon={<PassportIcon />}
      text={startCase(props.documentType.replaceAll('_', ' ').toLowerCase())}
      {...props}
    />
  )
}

export function DocumentUploadScreen() {
  const Alert = useAlert(DocumentUploadScreen.name)
  const documentUploadModalRef = useRef<BottomSheet>(null)
  const onError = useLogError()

  const {
    activeApplicationId: applicationId,
    activeApplicationUploads: requiredUploads,
    activeApplicationStatus,
  } = Hooks.useActiveApplication()

  // Use the new REST API mutation to generate document upload URLs
  const [generateDocumentUploadUrl] =
    Stores.CardApplicationApi.endpoints[
      'POST /card/onboarding/applications/:applicationId/document-upload-url'
    ].useMutation()

  // picked = selected in the app to be uploaded
  // uploaded = uploaded to the new REST API for review
  const [pickableDocuments, setPickableDocuments] = useState(() => new DocumentMap())

  // Track upload completion status
  const [uploadedDocuments, setUploadedDocuments] = useState<Set<DocumentUploadRequirement>>(
    new Set(),
  )

  // Check if we should redirect - when application status is not DOCUMENT_UPLOAD
  const shouldRedirectToApplicationInReview =
    activeApplicationStatus !== 'DOCUMENT_UPLOAD' ||
    !requiredUploads.length ||
    requiredUploads.every((upload) => uploadedDocuments.has(upload))

  // Initialize required documents based on the application requirements
  useEffect(() => {
    if (requiredUploads?.length && pickableDocuments.size === 0) {
      const initialDocuments = new DocumentMap(
        requiredUploads.map((upload) => [upload, null] as const),
      )
      setPickableDocuments(initialDocuments)
    }
  }, [requiredUploads, pickableDocuments.size])

  /** unique and sorted */
  const encounteredDocumentTypes = Array.from(
    new Set([
      ...(requiredUploads ?? []),
      ...Array.from(pickableDocuments.keys()),
      ...Array.from(uploadedDocuments),
    ]),
  ).toSorted()

  const { updateOptions } = useSardine()

  useEffect(() => {
    void updateOptions({ flow: 'application-document-upload' })
  }, [updateOptions])

  function handlePickedFile(
    file: ExpoDocumentPickerFile,
    documentType?: DocumentUploadRequirement,
  ) {
    documentType &&
      setPickableDocuments((prev) => new DocumentMap([...prev, [documentType, { file }]]))
  }

  const [selectedDocumentType, setSelectedDocumentType] = useState<DocumentUploadRequirement>()
  function handleOpenPicker(documentType: DocumentUploadRequirement) {
    setSelectedDocumentType(documentType)

    if (Platform.OS === 'web') {
      getDocumentAsync({ type: DEFAULT_FILE_TYPES, copyToCacheDirectory: true, multiple: false })
        .then(({ assets, output }) => {
          const asset = assets?.[0]
          if (!asset) return

          const { uri, mimeType, name } = asset

          if (uri && mimeType)
            handlePickedFile({ uri, mimeType, name, output: output ?? undefined }, documentType)
          else Alert.alert(`${name} is not a valid document`)
        })
        .catch(onError)
    } else {
      documentUploadModalRef.current?.snapToIndex(0)
    }
  }

  function renderRequiredDocument(documentType: DocumentUploadRequirement) {
    const isUploaded = uploadedDocuments.has(documentType)

    if (isUploaded) {
      return (
        <DocumentRow
          documentType={documentType}
          caption="Document uploaded"
          disabled
          IconEnd={
            <AntDesign
              name="checkcircle"
              size={24}
              color={mesaColorNames['semantic/fg/success #008557']}
              caption="Document uploaded"
            />
          }
        />
      )
    }

    const { file, uploading } = pickableDocuments.get(documentType) || {}

    return (
      <DocumentRow
        documentType={documentType}
        caption={file?.name ?? 'No file selected'}
        onPress={() => handleOpenPicker(documentType)}
        IconEnd={
          uploading ? (
            <Spinner color={mesaColorNames['semantic/fg/subtle #898D8F']} scale={0.8} />
          ) : file ? (
            <TrashIcon pathProps={{ stroke: mesaColorNames['semantic/fg/error #DB340B'] }} />
          ) : (
            <AntDesign
              name="pluscircleo"
              size={24}
              color={mesaColorNames['Mesa - Brand/Secondary #223843']}
            />
          )
        }
        onIconEndPress={() => {
          if (file) {
            setPickableDocuments((prev) => new DocumentMap([...prev, [documentType, null]]))
          } else {
            handleOpenPicker(documentType)
          }
        }}
      />
    )
  }

  const pickedButNotUploaded = pickableDocuments
    .entries()
    .flatMap(([documentType, file]) => (file ? ([[documentType, file]] as const) : []))
    .filter(([documentType]) => !uploadedDocuments.has(documentType))
    .toArray()

  const submittable = pickedButNotUploaded.length && applicationId

  const [uploading, setUploading] = useState(false)

  async function uploadDocuments() {
    try {
      if (!submittable) return
      setUploading(true)

      /** upload the files sequentially to avoid API rate limiting */
      for (const [documentType, { file }] of pickedButNotUploaded) {
        try {
          // Generate secure upload URL using the new REST API
          const uploadUrlResponse = await generateDocumentUploadUrl({
            params: { applicationId: applicationId },
            body: { fileName: file.name, documentType },
          })

          if ('error' in uploadUrlResponse) {
            throw new Error('Failed to generate upload URL', {
              cause: uploadUrlResponse.error,
            })
          }

          const uploadUrl = uploadUrlResponse.data.url

          // Set uploading state
          setPickableDocuments(
            (prev) => new DocumentMap([...prev, [documentType, { file, uploading: true }]]),
          )

          // Upload the file to the secure URL
          await uploadFile(file, uploadUrl)

          // Mark as uploaded and remove from pickable documents
          setUploadedDocuments((prev) => new Set([...prev, documentType]))
          setPickableDocuments((prev) => new DocumentMap([...prev, [documentType, null]]))
        } catch (error) {
          // Reset uploading state on error
          setPickableDocuments((prev) => new DocumentMap([...prev, [documentType, { file }]]))
          throw error
        }
      }
    } catch (error) {
      onError(error)
      Alert.alert('Failed to upload documents', 'Please try again')
    } finally {
      setUploading(false)
    }
  }

  if (shouldRedirectToApplicationInReview) {
    return <Redirect href={Routes.ApplicationInReview.route} />
  }

  return (
    <View flex={1} bg="$white1">
      <Components.ApplicationScreen>
        <View px="$4">
          <Components.ApplicationScreen.Content title="You're almost done">
            <Components.FormHelpText style={cardApplicationStyles.instructionText}>
              Additional document verification is required. Please provide the below requested
              documentation to proceed with review of your application. Once all requested
              documentation has been uploaded, tap Submit Documents to continue with your
              application.
            </Components.FormHelpText>
            {requiredUploads.length > 0 ? (
              encounteredDocumentTypes.map((documentType) => (
                <Fragment key={documentType}>{renderRequiredDocument(documentType)}</Fragment>
              ))
            ) : (
              <Loading />
            )}
          </Components.ApplicationScreen.Content>
        </View>
        <View px="$4">
          <YStack gap="$3" pt="$4" pb="$2">
            {submittable ? (
              <Button
                title={`Upload Document${pickedButNotUploaded.length > 1 ? 's' : ''}`}
                disabled={!submittable || uploading}
                loading={uploading}
                onPress={() => void uploadDocuments()}
              />
            ) : null}
            <Button
              buttonStyle={cardApplicationStyles.returnHomeButtonStyles}
              titleStyle={cardApplicationStyles.returnHomeTitleStyles}
              title="Return Home"
              onPress={() => router.replace(Routes.Main.route)}
            />
          </YStack>
        </View>
      </Components.ApplicationScreen>
      <DesignSystem.Modal.Upload
        title="Identity Verification"
        ref={documentUploadModalRef}
        onUploadPhoto={(file) => handlePickedFile(file, selectedDocumentType)}
        onUploadLibrary={(file) => handlePickedFile(file, selectedDocumentType)}
        onUploadFile={(file) => handlePickedFile(file, selectedDocumentType)}
      />
    </View>
  )
}
